/**
 * Theme Context for VenueFlow
 * 
 * Provides dark/light theme toggle with localStorage persistence
 * Default: dark theme (matching Operator portal)
 * 
 * Usage:
 *   import { useTheme } from './context/ThemeContext';
 *   const { theme, isDark, toggleTheme, statusColors } = useTheme();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ============================================================================
// STATUS COLORS (Used in both themes for row highlighting)
// MatchFlow Builder: New Submission, Rejected, Approved by Lumen8
// Operator View: Ready for Testing, Ready for Playout, Rejected, Approved by Lumen8
// ============================================================================
export const STATUS_COLORS = {
  'New Submission': { 
    bg: 'rgba(161, 161, 170, 0.15)', 
    border: 'rgba(161, 161, 170, 0.5)',
    dot: '#a1a1aa',
    text: '#a1a1aa',
  },
  'Ready for Testing': { 
    bg: 'rgba(251, 146, 60, 0.18)',  // Orange - new content needs Xpression ingest
    border: 'rgba(251, 146, 60, 0.5)',
    dot: '#fb923c',
    text: '#fb923c',
  },
  'Ready for Playout': { 
    bg: 'rgba(59, 130, 246, 0.18)',  // Blue - already in Xpression
    border: 'rgba(59, 130, 246, 0.5)',
    dot: '#3b82f6',
    text: '#3b82f6',
  },
  'Rejected': { 
    bg: 'rgba(239, 68, 68, 0.2)', 
    border: 'rgba(239, 68, 68, 0.6)',
    dot: '#ef4444',
    text: '#ef4444',
  },
  'Approved by Lumen8': { 
    bg: 'rgba(34, 197, 94, 0.2)', 
    border: 'rgba(34, 197, 94, 0.6)',
    dot: '#22c55e',
    text: '#22c55e',
  },
  // Legacy statuses (for backward compatibility with existing data)
  'Change Alert': { 
    bg: 'rgba(251, 191, 36, 0.18)', 
    border: 'rgba(251, 191, 36, 0.5)',
    dot: '#fbbf24',
    text: '#fbbf24',
  },
  'Resubmitted': { 
    bg: 'rgba(251, 146, 60, 0.18)', 
    border: 'rgba(251, 146, 60, 0.5)',
    dot: '#fb923c',
    text: '#fb923c',
  },
  'Sent to Incoder': { 
    bg: 'rgba(168, 85, 247, 0.18)', 
    border: 'rgba(168, 85, 247, 0.5)',
    dot: '#a855f7',
    text: '#a855f7',
  },
  'Example Content': { 
    bg: 'rgba(148, 163, 184, 0.1)', 
    border: 'rgba(148, 163, 184, 0.3)',
    dot: '#94a3b8',
    text: '#94a3b8',
  },
};

// ============================================================================
// SHARED VALUES (Same in both themes)
// ============================================================================
const SHARED = {
  // Brand colors
  accent: '#2fd6b5',
  accentHover: '#26b89a',
  lime: '#cdde25',
  limeHover: '#b8c821',
  
  // Semantic colors
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  
  // Transitions
  transition: 'all 0.15s ease',
  transitionFast: 'all 0.1s ease',
  transitionSlow: 'all 0.25s ease',
  
  // Border radius
  radiusSm: '6px',
  radius: '10px',
  radiusMd: '12px',
  radiusLg: '16px',
  radiusXl: '20px',
  radiusFull: '9999px',
  
  // Status colors reference
  statusColors: STATUS_COLORS,
};

// ============================================================================
// DARK THEME (Default - matches Operator Dashboard)
// ============================================================================
export const DARK_THEME = {
  ...SHARED,
  mode: 'dark',
  
  // Backgrounds
  bg: '#0a0a0f',
  bgElevated: '#12121a',
  bgCard: '#16161f',
  bgHover: '#1e1e2a',
  background: '#0a0a0f',
  
  // Primary (for compatibility with existing theme.js)
  primary: '#2fd6b5',
  primaryHover: '#26b89a',
  primaryLight: 'rgba(47, 214, 181, 0.15)',
  secondary: '#cdde25',
  secondaryHover: '#b8c821',
  secondaryLight: 'rgba(205, 222, 37, 0.15)',
  
  // Dark elements
  dark: '#0a0a0f',
  darkHover: '#16161f',
  darkMuted: '#1e1e2a',
  
  // Text colors
  text: '#ffffff',
  textPrimary: '#ffffff',
  textSecondary: '#a1a1aa',
  textMuted: '#71717a',
  textOnDark: '#ffffff',
  textOnPrimary: '#0a0a0f',
  
  // Borders
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.15)',
  line: 'rgba(255, 255, 255, 0.08)',
  lineLight: 'rgba(255, 255, 255, 0.05)',
  lineDark: 'rgba(255, 255, 255, 0.15)',
  
  // Surfaces
  surface: '#16161f',
  surface2: '#1e1e2a',
  
  // Status colors with light variants
  successLight: 'rgba(34, 197, 94, 0.15)',
  warningLight: 'rgba(245, 158, 11, 0.15)',
  errorLight: 'rgba(239, 68, 68, 0.15)',
  infoLight: 'rgba(59, 130, 246, 0.15)',
  
  // Interactive states
  hover: 'rgba(47, 214, 181, 0.08)',
  selected: 'rgba(47, 214, 181, 0.12)',
  focus: '#2fd6b5',
  
  // Shadows (darker for dark theme)
  shadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
  shadowSm: '0 1px 4px rgba(0, 0, 0, 0.3)',
  shadowMd: '0 4px 12px rgba(0, 0, 0, 0.5)',
  shadowLg: '0 8px 24px rgba(0, 0, 0, 0.6)',
  shadowXl: '0 16px 32px rgba(0, 0, 0, 0.7)',
  shadowRow: '0 1px 4px rgba(0, 0, 0, 0.3)',
  shadowRowHover: '0 4px 12px rgba(0, 0, 0, 0.4)',
  shadowRowDrag: '0 8px 24px rgba(0, 0, 0, 0.5)',
  shadowCategory: '0 2px 8px rgba(0, 0, 0, 0.4)',
  
  // Table specific
  tableHeader: '#12121a',
  tableRowHover: 'rgba(47, 214, 181, 0.06)',
  tableRowSelected: 'rgba(47, 214, 181, 0.12)',
  tableRowAlt: '#14141c',
  
  // Category styles
  categoryBg: '#12121a',
  categoryBorder: 'rgba(255, 255, 255, 0.1)',
  categoryText: '#ffffff',
  
  // Special elements
  chip: '#1e1e2a',
  chipText: '#a1a1aa',
  badge: '#cdde25',
  badgeText: '#0a0a0f',
};

// ============================================================================
// LIGHT THEME (Trevor's preference)
// ============================================================================
export const LIGHT_THEME = {
  ...SHARED,
  mode: 'light',
  
  // Backgrounds
  bg: '#f7f8fa',
  bgElevated: '#ffffff',
  bgCard: '#ffffff',
  bgHover: '#f3f4f6',
  background: '#f7f8fa',
  
  // Primary (for compatibility)
  primary: '#22BA91',
  primaryHover: '#1da37f',
  primaryLight: '#e8f7f3',
  secondary: '#cdde25',
  secondaryHover: '#b8c821',
  secondaryLight: '#f8faed',
  
  // Dark elements
  dark: '#141414',
  darkHover: '#2a2a2a',
  darkMuted: '#374151',
  
  // Text colors
  text: '#141414',
  textPrimary: '#141414',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  textOnDark: '#ffffff',
  textOnPrimary: '#ffffff',
  
  // Borders
  border: '#e5e7eb',
  borderHover: '#d1d5db',
  line: '#e5e7eb',
  lineLight: '#f3f4f6',
  lineDark: '#d1d5db',
  
  // Surfaces
  surface: '#ffffff',
  surface2: '#f3f4f6',
  
  // Status colors with light variants
  successLight: '#dcfce7',
  warningLight: '#fef3c7',
  errorLight: '#fee2e2',
  infoLight: '#dbeafe',
  
  // Interactive states
  hover: 'rgba(34, 186, 145, 0.08)',
  selected: 'rgba(34, 186, 145, 0.12)',
  focus: '#22BA91',
  
  // Shadows (lighter for light theme)
  shadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.04)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
  shadowLg: '0 10px 20px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
  shadowXl: '0 20px 30px rgba(0, 0, 0, 0.10), 0 10px 15px rgba(0, 0, 0, 0.05)',
  shadowRow: '0 1px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  shadowRowHover: '0 4px 12px rgba(0, 0, 0, 0.10), 0 2px 4px rgba(0, 0, 0, 0.04)',
  shadowRowDrag: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.08)',
  shadowCategory: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
  
  // Table specific
  tableHeader: '#f9fafb',
  tableRowHover: '#f9fafb',
  tableRowSelected: 'rgba(34, 186, 145, 0.08)',
  tableRowAlt: '#fafbfc',
  
  // Category styles
  categoryBg: '#f9fafb',
  categoryBorder: '#e5e7eb',
  categoryText: '#374151',
  
  // Special elements
  chip: '#f3f4f6',
  chipText: '#374151',
  badge: '#cdde25',
  badgeText: '#141414',
};

// ============================================================================
// FONTS (Same for both themes)
// ============================================================================
export const FONTS = {
  family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  xs: "11px",
  sm: "12px",
  base: "14px",
  md: "15px",
  lg: "16px",
  xl: "18px",
  "2xl": "20px",
  "3xl": "24px",
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.625,
};

// ============================================================================
// THEME CONTEXT
// ============================================================================
const ThemeContext = createContext({
  theme: DARK_THEME,
  isDark: true,
  toggleTheme: () => {},
  setTheme: () => {},
  statusColors: STATUS_COLORS,
  fonts: FONTS,
});

export const useTheme = () => useContext(ThemeContext);

// ============================================================================
// THEME PROVIDER
// ============================================================================
export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('venueflow_theme');
      return saved !== 'light'; // Default to dark
    } catch {
      return true;
    }
  });
  
  const theme = isDark ? DARK_THEME : LIGHT_THEME;
  
  // Save preference and update CSS variables
  useEffect(() => {
    try {
      localStorage.setItem('venueflow_theme', isDark ? 'dark' : 'light');
    } catch {}
    
    // Update body class for global CSS
    document.body.classList.toggle('theme-dark', isDark);
    document.body.classList.toggle('theme-light', !isDark);
    
    // Apply theme directly to body for immediate visual feedback
    document.body.style.backgroundColor = theme.bg;
    document.body.style.color = theme.text;
    document.documentElement.style.backgroundColor = theme.bg;
    
    // Update CSS variables on root element for global theming
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === 'string') {
        root.style.setProperty(`--vf-${key}`, value);
      }
    });
  }, [isDark, theme]);
  
  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);
  
  const setTheme = useCallback((mode) => {
    setIsDark(mode === 'dark');
  }, []);
  
  return (
    <ThemeContext.Provider value={{
      theme,
      isDark,
      toggleTheme,
      setTheme,
      statusColors: STATUS_COLORS,
      fonts: FONTS,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================================
// HELPER: Get theme for non-context usage (e.g., constants files)
// ============================================================================
export function getTheme(mode = 'dark') {
  return mode === 'light' ? LIGHT_THEME : DARK_THEME;
}

// ============================================================================
// HELPER: Get status color styling
// ============================================================================
export function getStatusStyle(status) {
  return STATUS_COLORS[status] || { 
    bg: 'transparent', 
    border: 'transparent', 
    dot: '#6b7280',
    text: '#6b7280',
  };
}

export default ThemeContext;
